import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { trackStoreClick } from "@/lib/analytics";
import { appStoreUrlFor, playStoreUrlFor } from "@/lib/seo";
import { detectPlatform } from "@/lib/stores";
import { StoreBadges } from "@/components/common/StoreBadges";

/**
 * /download (+ /en/download) — a trackable waypoint to the app stores. A visit
 * to this route equals a store click, so it can be measured by hosting/server
 * analytics even without a tag manager.
 *
 * - Android devices  → Google Play
 * - iPhone / iPad    → App Store
 * - Everything else  → shows both badges (no redirect)
 * - `?store=play` or `?store=ios` forces a destination (handy for QR codes/ads).
 * - /android and /ios are short links that always force one store
 *   (`forceStore` prop), e.g. refoundapp.ca/android → Google Play.
 *
 * Kept out of search engines (noindex).
 */
const DownloadRedirect = ({ forceStore }: { forceStore?: "play" | "ios" }) => {
  const { language } = useLanguage();
  const [params] = useSearchParams();
  const forced = forceStore ?? params.get("store");

  const appStoreUrl = appStoreUrlFor(language);
  const playStoreUrl = playStoreUrlFor(language);

  const target = useMemo<"app_store" | "google_play" | null>(() => {
    if (forced === "play" || forced === "android" || forced === "google") return "google_play";
    if (forced === "ios" || forced === "apple" || forced === "appstore") return "app_store";
    const platform = detectPlatform();
    if (platform === "android") return "google_play";
    if (platform === "ios") return "app_store";
    return null;
  }, [forced]);

  const url = target === "google_play" ? playStoreUrl : target === "app_store" ? appStoreUrl : null;

  useEffect(() => {
    // Keep this utility route out of search results.
    const robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex, follow");
    document.head.appendChild(robots);

    let timer: number | undefined;
    if (target && url) {
      trackStoreClick(target, "download_redirect", language);
      timer = window.setTimeout(() => window.location.replace(url), 120);
    }

    return () => {
      if (timer) window.clearTimeout(timer);
      robots.remove();
    };
  }, [language, target, url]);

  const isFr = language === "fr";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center">
      <img src="/logo-test-myreceipt-17.png" alt="ReFound" className="mb-8 h-7" />
      {url ? (
        <>
          <p className="text-lg text-foreground">
            {target === "google_play"
              ? isFr
                ? "Redirection vers Google Play…"
                : "Redirecting to Google Play…"
              : isFr
                ? "Redirection vers l'App Store…"
                : "Redirecting to the App Store…"}
          </p>
          <a
            href={url}
            className="mt-4 text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            {isFr ? "Cliquez ici si rien ne se passe" : "Click here if nothing happens"}
          </a>
        </>
      ) : (
        <>
          <p className="text-lg text-foreground">
            {isFr
              ? "Téléchargez ReFound gratuitement sur iPhone ou Android."
              : "Download ReFound for free on iPhone or Android."}
          </p>
          <div className="mt-8">
            <StoreBadges location="download_page" />
          </div>
        </>
      )}
    </div>
  );
};

export default DownloadRedirect;
